<script lang="ts" setup>
export interface OfficesMapListProps {
    offices: MapMarker[]
}

const props = defineProps<OfficesMapListProps>()
const officesMapStore = useOfficesMapStore()

const { data, sendEvent } = useTracker()
const config = useRuntimeConfig()

const handleClickOnOffice = (office: MapMarker, event: Event): void => {
    // WCAG 2.4.3: Store the trigger element for returning focus when popup closes
    officesMapStore.value.triggerElement = event.currentTarget as HTMLElement
    officesMapStore.value.activeOffice = office
    officesMapStore.value.activeMarker = office

    // WCAG 2.4.3: Move focus to the map popup after selection
    // Google Maps InfoWindow renders asynchronously, so we need to wait for it
    moveFocusToMapPopup()

    sendEvent({
        data: {
            ...data,
            click: `${office.data.address}, ${office.data.zip_code} ${office.data.city}`,
        },
    })
}

/**
 * WCAG 2.4.3: Move focus to the map popup when selecting from the list
 * This ensures screen reader users are taken to the newly opened content
 */
const moveFocusToMapPopup = (): void => {
    // Multiple attempts to find and focus the popup, as Google Maps renders asynchronously
    const attemptFocus = (attempt: number = 0): void => {
        if (attempt > 10) return // Max 10 attempts (2 seconds total)

        nextTick(() => {
            // Find the map modal close button (most reliable target)
            const mapModal = document.querySelector('.map-modal')
            const closeButton = mapModal?.querySelector('button[aria-label]') as HTMLElement

            if (closeButton) {
                closeButton.focus()
            } else {
                // If not found yet, try again after a delay
                setTimeout(() => attemptFocus(attempt + 1), 200)
            }
        })
    }

    // Start focus attempts after a short delay to let Google Maps start rendering
    setTimeout(() => attemptFocus(0), 100)
}
</script>
<template>
  <ul class="overflow-auto ms-n2">
    <li
      v-for="(office, index) in props.offices"
      :key="`pcmo-${index}`"
      class="p-2 rounded-1"
      :class="{
        'bg-secondary-light':
          officesMapStore.activeMarker?.data.slug ===
          office.data.slug,
      }"
    >
      <button
        type="button"
        class="d-flex align-items-center bg-transparent border-0 p-0 w-100 text-start text-decoration-underline"
        :class="{
          'text-secondary':
            officesMapStore.activeOffice?.data.slug ===
            office.data.slug,
          'text-black':
            officesMapStore.activeOffice?.data.slug !==
            office.data.slug,
        }"
        :aria-current="officesMapStore.activeMarker?.data.slug === office.data.slug ? 'true' : undefined"
        :aria-pressed="officesMapStore.activeMarker?.data.slug === office.data.slug"
        :aria-label="`${office.data.address}, ${office.data.zip_code} ${office.data.city}`"
        :aria-labelledby="`${office.data.address}, ${office.data.zip_code} ${office.data.city}`"
        @mouseover="officesMapStore.hoveredMarker = office"
        @mouseleave="officesMapStore.hoveredMarker = undefined"
        @click="handleClickOnOffice(office, $event)"
      >
        <img
          :src="
            officesMapStore.activeOffice?.data.slug ===
              office.data.slug
              ? `${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/pin-secondary-2.svg?v=1`
              : `${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/pin-black-2.svg?v=1`
          "
          class="d-block me-3"
          loading="lazy"
          decoding="async"
          :width="22"
          :height="32"
          alt=""
        >
        {{ office.data.address }}, {{ office.data.zip_code }}
        {{ office.data.city }}
      </button>
    </li>
  </ul>
</template>
